import { defineStore } from 'pinia'
import { computed, ref, watch } from 'vue'
import { useAuth0 } from '@auth0/auth0-vue'

const usedCompanyFields = [
  {
    path: 'websiteUrl',
    label: 'Website URL',
    description: `Used in the site's footer`,
    important: false,
  },
  {
    path: 'email',
    label: 'Email',
    description: `Used in the site's footer`,
    important: false,
  },
  {
    path: 'phone',
    label: 'Phone',
    description: `Used in the site's footer`,
    important: false,
  },
  {
    path: 'logoUrl',
    label: 'Logo',
    description: `Used in the vacancy pages`,
    important: false,
  },
  {
    path: 'coverPhotoUrl',
    label: 'Cover photo',
    description: `Used in the vacancy pages`,
    important: false,
  },
  {
    path: 'carouselImagesUrls',
    label: 'Carousel images',
    description: `If available, used in most pages (homepage, vacancy list, vacancy pages, etc)`,
    important: false,
  },
  {
    path: 'shortDescription',
    label: 'Short description',
    description: `If available, used in vacancy pages`,
    important: false,
  },
  {
    path: 'socialLinks',
    label: 'Social links (Facebook, LinkedIn, etc)',
    description: `Used in the site's footer`,
    important: false,
  },
  {
    path: 'location.geo.lat',
    label: 'Address',
    description: `Used in the homepage and vacancy pages`,
    important: false,
  },
]

export const useTOOStore = defineStore('too', () => {
  const { isAuthenticated, getAccessTokenSilently } = useAuth0()

  const site = ref(null)
  async function fetchSite(siteSlug) {
    if (site.value) return site.value

    const headers = await generateHeaders()
    const response = await fetch(
      `${import.meta.env.VITE_TOO_URL}/management/v1/sites/${siteSlug}`,
      {
        headers,
      },
    )
    const { data } = await response.json()
    site.value = data
    return site.value
  }

  const companies = ref([])
  watch(isAuthenticated, (authenticated) => {
    if (authenticated) {
      fetchCompanies()
    }
  })

  async function fetchCompanies() {
    if (companies.value.length) return companies.value

    const headers = await generateHeaders()
    const fetchPage = async (pageNumber) => {
      const params = new URLSearchParams({
        page: pageNumber,
        size: 100,
      })
      const response = await fetch(
        `${import.meta.env.VITE_TOO_URL}/management/v1/companies?${params.toString()}`,
        {
          headers,
        },
      )
      const { data, meta } = await response.json()

      companies.value = companies.value.concat(data)
      if (meta.page.totalPages > pageNumber) {
        await fetchPage(pageNumber + 1)
      }
    }
    await fetchPage(1)

    return companies.value
  }

  async function checkCompanyCompleteness(company) {
    const headers = await generateHeaders()

    const [responseCompany, responseSites] = await Promise.all([
      fetch(`${import.meta.env.VITE_TOO_URL}/management/v1/companies/${company.id}`, {
        headers,
      }),
      fetch(`${import.meta.env.VITE_TOO_URL}/management/v1/companies/${company.id}/sites`, {
        headers,
      }),
    ])

    const { data: sites } = await responseSites.json()
    let siteFound = false
    sites.forEach((item) => {
      if (site.value.id === item.siteId) {
        siteFound = true
      }
    })

    const { data: companyInfo } = await responseCompany.json()
    const missingProperties = getMissingProperties(companyInfo, usedCompanyFields)
    if (!siteFound) {
      missingProperties.push({
        path: 'site',
        label: 'Link with site',
        description: `"${site.value.siteSlug}" must be part of the company's allowed sites`,
        important: true,
      })
    }
    return missingProperties
  }

  async function updateStoryblokToken(accessToken) {
    const headers = await generateHeaders()
    headers['Content-Type'] = 'application/json'

    const response = await fetch(
      `${import.meta.env.VITE_TOO_URL}/management/v1/sites/${site.value.id}`,
      {
        method: 'PUT',
        headers,
        body: JSON.stringify({
          ...site.value,
          storyblokToken: accessToken,
        }),
      },
    )
    if (response.ok) {
      return true
    }
    return false
  }

  async function generateHeaders() {
    const accessToken = await getAccessTokenSilently()
    const headers = {
      Authorization: `Bearer ${accessToken}`,
    }
    return headers
  }

  return {
    site: computed(() => site),
    fetchSite,
    companies,
    checkCompanyCompleteness,
    updateStoryblokToken,
  }
})

function getMissingProperties(obj, requiredFields) {
  return requiredFields.filter((field) => {
    const value = field.path.split('.').reduce((current, key) => current?.[key], obj)

    // Check if null/undefined OR if it's an empty array
    return (
      value == null ||
      (typeof value === 'string' && value.trim() === '') ||
      (Array.isArray(value) && value.length === 0)
    )
  })
}
